import store from "../store";
import history from "../routes/History";
import { ROUTE_PATHS } from "./RoutesPaths";
import { EVENT_VIEW_TABS } from "./eventViewTabs";
import { setCompanyNotificationSeenRequest } from "../store/CompanyPortal/Notifications/actions";

/**
 * Company Portal notification deep-linking (PROLINE-2854).
 *
 * Maps a notification `click_action` to a company-portal route. `action_id`
 * (entity id, as a string) fills the `:id` segment for parameterized routes.
 * Destinations below match the product notifications catalog (Case → Click Action):
 *   - `update_company`            -> View company profile          (profile updated by admin)
 *   - `list_company_edit_requests`-> View company profile          (edit request approved/rejected)
 *   - `show_event`                -> Event view page               (admin created/edited an event; action_id = event id)
 *   - `list_event_requests`       -> Event view page (applicants tab) (joining request received / cancelled; action_id = event id)
 *   - `list_event_evaluations`    -> Event view page (evaluations tab) (evaluation ready; action_id = evaluation id,
 *                                                                     event_id = event id used for the deep link).
 *                                                                     Falls back to the events list if `event_id` is absent.
 *
 * Not mapped (only mark-as-seen, no navigation):
 *   - `null` on auto-cancelled joining requests — catalog says "No redirection needed".
 *   - `null` on supervisor logistics reports — catalog wants "View report details",
 *     but the company portal has no report/logistics-issue screen yet.
 *
 * `paramSource` selects which field fills the `:id` segment ("action_id" by
 * default; "event_id" for evaluations, whose action_id is the evaluation id).
 */
export const companyPortalNotificationsActionsList = [
  {
    action: "update_company",
    route: ROUTE_PATHS.companySettingsProfile,
    hasParam: false,
  },
  {
    action: "list_company_edit_requests",
    route: ROUTE_PATHS.companySettingsProfile,
    hasParam: false,
  },
  {
    action: "show_event",
    route: ROUTE_PATHS.companyViewEvent,
    hasParam: true,
  },
  {
    action: "list_event_requests",
    route: ROUTE_PATHS.companyViewEvent,
    hasParam: true,
    state: { initialTab: EVENT_VIEW_TABS.applicants },
  },
  {
    action: "list_event_evaluations",
    route: ROUTE_PATHS.companyViewEvent,
    hasParam: true,
    paramSource: "event_id",
    state: { initialTab: EVENT_VIEW_TABS.evaluations },
    fallbackRoute: ROUTE_PATHS.companyEvents,
  },
  // PROLINE-3641: the other party uploaded a report. `action_id` is the client
  // report id, `event_id` the event to open. A recipient without Manage Reports
  // `can_view` still gets the notification — the Client Reports tab then falls
  // back to Basic Info on its own (no error screen).
  {
    action: "show_client_report",
    route: ROUTE_PATHS.companyViewEvent,
    hasParam: true,
    paramSource: "event_id",
    state: { initialTab: EVENT_VIEW_TABS.clientReports },
    fallbackRoute: ROUTE_PATHS.companyEvents,
  },
];

export const setCompanyNotificationSeen = (id) => {
  store.dispatch(setCompanyNotificationSeenRequest(id));
};

export const handleCompanyPortalNotificationClick = ({
  id,
  click_action,
  action_id,
  event_id,
  is_seen,
}) => {
  if (!is_seen) setCompanyNotificationSeen(id);

  const target = companyPortalNotificationsActionsList.find(
    (elt) => elt.action === click_action,
  );
  if (!target?.route) return;

  if (!target.hasParam) {
    history.push(target.route, target.state);
    return;
  }

  const paramValue = target.paramSource === "event_id" ? event_id : action_id;
  if (paramValue == null) {
    if (target.fallbackRoute) history.push(target.fallbackRoute);
    return;
  }

  history.push(target.route.replace(":id", paramValue), target.state);
};
